import {
  FormValues,
  type FormActions,
  type FormConfig,
  type FormResults,
} from '@wix/form-viewer';
import { FormViewerContext, FormViewerHandle } from '@wix/form-viewer/widget';
import { useSettings } from '@wix/tpa-settings/react';
import { withSlotsPlaceholders } from '@wix/widget-plugins-ooi';
import {
  useBi,
  useEnvironment,
  useErrorBoundary,
  useExperiments,
  useTranslation,
  WidgetProps,
} from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import React, { FC, Suspense, useCallback, useEffect, useState } from 'react';
import {
  CartConflictErrorType,
  EmptyStateErrorType,
  FormError,
  GenericErrorType,
} from '../../../types/errors';
import { ExperimentsConsts } from '../../../consts/experiments';
import { FormStatus } from '../../../types/form-state';
import { DialogType, PaymentMethod } from '../../../types/types';
import { mapToArray } from '../../../utils';
import { getErrorByType, hasErrorOfType } from '../../../utils/errors/errors';
import { OfferedAsType } from '../../../utils/mappers/serviceMapper/utils/payment.mapper';
import {
  FormState,
  IntakeForm,
  IntakeFormStatus,
  ServiceData,
  SlotService,
} from '../../../utils/state/types';
import { Status } from '@wix/ambassador-intake-forms-v1-intake-form-submission/types';
import { ApprovalMode } from '@wix/ambassador-intake-forms-v1-intake-form/types';
import { Modal } from '../Modal';
import { isServiceExist } from '../../../utils/validations/Validations';
import settingsParams from '../settingsParams';
import { FormControllerActions } from '../Actions/actions';
import { EditorContextProvider } from '../Hooks/useEditorContext';
import { FormActionsProvider } from '../Hooks/useFormActions';
import { useBfcacheStatusReset } from '../Hooks/useBfcacheStatusReset';
import { FormComponentContextProvider } from '../Hooks/useFormComponentContext';
import useValidateWixForm from '../Hooks/useValidateWixForm';
import { AddonsDetails } from './AddonsDetails';
import { RelatedProducts } from './RelatedProducts';
import { AddonsNotification } from './AddonsNotification';
import AlreadyBookedModal from './AlreadyBookedModal';
import BackButton from './BackButton/BackButton';
import BookingDetails from './BookingDetails/BookingDetails';
import ButtonsContainer from './ButtonsContainer';
import { CartConflictMessage } from './CartConflictMessage/CartConflictMessage';
import CartErrorModal from './CartErrorModal';
import CartModal from './CartModal/CartModal';
import { CouponCheckbox } from './CouponCheckbox';
import { WidgetDataHooks } from './dataHooks';
import EmptyStatePage from './EmptyStatePage/EmptyStatePage';
import Header from './Header';
import { handleIntakeFormEmailValidation } from './IntakeFormSection/handleIntakeFormEmailValidation';
import IntakeFormSection from './IntakeFormSection/IntakeFormSection';
import NumberOfParticipantsDropdown from './NumberOfParticipantsDropdown/NumberOfParticipantsDropdown';
import { PaymentSelectionContainer } from './PaymentSelectionContainer/PaymentSelectionContainer';
import { PaymentSummary } from './PaymentSummary/PaymentSummary';
import { ServiceInCartNotification } from './ServiceInCartNotification';
import SmsNotification from './SmsNotification/SmsNotification';
import { UnavailableSlotMessage } from './UnavailableSlotMessage';
import { classes, st } from './Widget.st.css';
import WixForm from './WixForm';
import ChevronLeft from 'wix-ui-icons-common/on-stage/ChevronLeftSmall';
import ChevronRight from 'wix-ui-icons-common/on-stage/ChevronRightSmall';
import BookForSomeoneElseCheckbox from './BookForSomeoneElseCheckbox';
import BookForSomeoneElseBanner from './BookForSomeoneElseBanner';
import { getIsCollectParticipantFormActive } from '../../../utils/collectParticipantForm';
import ParticipantsPager from './ParticipantsPager';
import ParticipantActionButton, {
  ParticipantActionButtonDataHooks,
} from './ParticipantActionButton';
import Plus from 'wix-ui-icons-common/on-stage/Plus';
import Delete from 'wix-ui-icons-common/on-stage/Delete';

const Dialog = loadable(() => import('./Dialog/Dialog'));
const Toast = loadable(() => import('./Toast/Toast'));
const PolicyDescription = loadable(
  () => import('./PolicyDescription/PolicyDescription'),
);

const showGlobalLoaderList = [
  FormStatus.PROCESSING_SUBMIT_ACTION,
  FormStatus.REINITIALIZING,
];

// bulkCreateBooking accepts at most 12 entries (one booking per participant form).
const MAX_PARTICIPANTS_PER_BOOKING = 12;

export type ControllerProps = {
  actions: FormControllerActions;
} & FormState &
  WidgetProps & {
    wfActions: FormActions;
    wfConfig: FormConfig;
    wfResults: FormResults;
  };

const Widget: FC<WidgetProps<ControllerProps>> = (widgetProps) => {
  const {
    intakeForms,
    serviceData,
    actions,
    isPricingPlanInstalled,
    formSelectedSlot,
    businessInfo,
    isCart,
    couponInfo,
    memberDetails,
    isLoggedIn,
    errors,
    editorContext,
    status,
    dialog,
    cartModal,
    isServiceInCart,
    isUpsellPluginInstalled,
    existingBookingDetails,
    wixForms,
    selectedNumberOfParticipants,
    isSMSAutomationsEnabled,
    isSlotUnavailable,
    selectedProductAddons,
    relatedProducts,
    storesAppInstance,
    isBookingForSomeoneElse,
  } = widgetProps;
  const { isMobile, isSSR, isRTL } = useEnvironment();
  const { error } = useErrorBoundary();
  const settings = useSettings();
  const bi = useBi();
  const { experiments } = useExperiments();
  const { t } = useTranslation();
  const dateRegionalSettingsLocale = businessInfo?.dateRegionalSettingsLocale!;
  const { validateWixForm } = useValidateWixForm();
  const wixFormRef = React.useRef<FormViewerHandle>(null);
  const [wixFormValues, setWixFormValues] = useState<FormValues>({});
  // Per-participant forms (collect-participant-form flow). The list is the source of
  // truth for the collected values; only the active participant's form is mounted.
  const [participantForms, setParticipantForms] = useState<FormValues[]>([{}]);
  const [activeParticipantIndex, setActiveParticipantIndex] = useState(0);

  const isBookForSomeoneElseEnabled = experiments.enabled(
    ExperimentsConsts.BookForSomeoneElse,
  );
  const isBookForSomeoneElseAvailable =
    isBookForSomeoneElseEnabled &&
    !!serviceData?.allowBookForSomeoneElse &&
    !hasPricingPlanOnlyService(serviceData);
  const isBookForSomeoneElseActive =
    isBookForSomeoneElseAvailable && isBookingForSomeoneElse;

  const isCollectParticipantFormActive = getIsCollectParticipantFormActive(
    serviceData,
    experiments.enabled(ExperimentsConsts.CollectParticipantForm),
  );
  // Cap participants by the open slots (availableSpots is already folded into
  // maxNumberOfParticipantsWithoutPP = min(maxParticipantsPerBooking, openSpots)),
  // ignoring pricing-plan credits since plans are blocked in this flow, and never
  // exceeding the bulkCreateBooking limit.
  const maxParticipants = Math.min(
    serviceData?.maxNumberOfParticipantsWithoutPP ??
      serviceData?.maxNumberOfParticipants ??
      1,
    MAX_PARTICIPANTS_PER_BOOKING,
  );
  // Pricing plans (use-plan / buy-plan / membership) cannot be combined with more
  // than one participant. When a plan is selected, block adding participants; once
  // there is more than one participant, plan options are excluded entirely.
  const isPayingWithPlan = mapToArray<SlotService>(
    serviceData?.slotServices ?? {},
  ).some(
    // selectedPaymentType is MEMBERSHIP for both buy-plan and use-plan options.
    (slotService) =>
      slotService.selectedPaymentType === PaymentMethod.MEMBERSHIP,
  );
  const hasMultipleParticipants =
    isCollectParticipantFormActive && participantForms.length > 1;

  const [hasLoggedInViaModal, setHasLoggedInViaModal] = useState(false);
  const isRelatedProductsOnServicesEnabled = experiments.enabled(
    ExperimentsConsts.RelatedProductsOnServices,
  );
  // Gates every pending-approval behaviour (login wall + pending dialog). OFF ⇒ pre-feature flow.
  const isIntakeFormsPendingApprovalEnabled = experiments.enabled(
    ExperimentsConsts.IntakeFormsPendingApproval,
  );
  const handleWixFormChange = useCallback(
    (values: FormValues) => {
      setWixFormValues(values);
      handleIntakeFormEmailValidation(values, intakeForms, actions, bi);
    },
    [intakeForms, actions, bi],
  );

  const handleParticipantFormChange = useCallback(
    (values: FormValues) => {
      setParticipantForms((prev) => {
        const next = [...prev];
        next[activeParticipantIndex] = values;
        return next;
      });
    },
    [activeParticipantIndex],
  );

  // Only the active participant's <FormViewer> is mounted, so navigating away
  // unmounts it. onChange doesn't reliably flush the last edits back across the
  // mount/unmount, so before switching we snapshot the live form values straight
  // from the handle into the participant list (the source of truth for submission).
  const commitActiveParticipantForm = useCallback(() => {
    const values = wixFormRef.current?.getValues?.();
    if (!values) {
      return;
    }
    setParticipantForms((prev) => {
      const next = [...prev];
      next[activeParticipantIndex] = values;
      return next;
    });
  }, [activeParticipantIndex]);

  const goToParticipant = useCallback(
    (index: number) => {
      commitActiveParticipantForm();
      setActiveParticipantIndex(index);
    },
    [commitActiveParticipantForm],
  );

  // Keep the participant count (which drives pricing + the payment summary) in sync
  // with the number of rendered participant forms.
  const serviceSlotKey = Object.keys(serviceData?.slotServices ?? {})[0];
  const participantSlotService = serviceData?.slotServices?.[serviceSlotKey];

  useEffect(() => {
    if (
      isCollectParticipantFormActive &&
      participantSlotService &&
      !participantSlotService.participantAddons
    ) {
      actions.updateServiceSlot({ participantAddons: [{}] }, serviceSlotKey);
    }
  }, [
    isCollectParticipantFormActive,
    participantSlotService,
    serviceSlotKey,
    actions,
  ]);

  const addParticipant = useCallback(() => {
    commitActiveParticipantForm();
    const nextCount = participantForms.length + 1;
    setActiveParticipantIndex(participantForms.length);
    setParticipantForms((prev) => [...prev, {}]);
    if (isCollectParticipantFormActive && participantSlotService) {
      actions.updateServiceSlot(
        {
          participantAddons: [
            ...(participantSlotService.participantAddons ?? []),
            {},
          ],
        },
        serviceSlotKey,
      );
    }
    actions.setNumberOfParticipants(nextCount, serviceSlotKey);
  }, [
    commitActiveParticipantForm,
    participantForms.length,
    actions,
    serviceSlotKey,
    isCollectParticipantFormActive,
    participantSlotService,
  ]);

  const removeParticipant = useCallback(() => {
    if (participantForms.length <= 1) {
      return;
    }
    const nextCount = participantForms.length - 1;
    // The active (removed) form is discarded, so no need to commit it. Any other
    // participant's values already live in the list.
    setParticipantForms((prev) =>
      prev.filter((_, index) => index !== activeParticipantIndex),
    );
    if (isCollectParticipantFormActive && participantSlotService) {
      actions.updateServiceSlot(
        {
          participantAddons: (
            participantSlotService.participantAddons ?? []
          ).filter((_, index) => index !== activeParticipantIndex),
        },
        serviceSlotKey,
      );
    }
    // Stay on the previous participant (or the first one when removing index 0).
    setActiveParticipantIndex((index) => (index > 0 ? index - 1 : 0));
    actions.setNumberOfParticipants(nextCount, serviceSlotKey);
  }, [
    participantForms.length,
    activeParticipantIndex,
    actions,
    serviceSlotKey,
    isCollectParticipantFormActive,
    participantSlotService,
  ]);

  useEffect(() => {
    if (
      status === FormStatus.INITIALIZING ||
      status === FormStatus.REINITIALIZING
    ) {
      actions.initializeWidget();
    }
  }, [status, actions]);

  useBfcacheStatusReset({
    status,
    setFormStatus: actions.setFormStatus,
    experiments,
  });

  useEffect(() => {
    if (!isBookForSomeoneElseActive) {
      setWixFormValues({ ...wixFormValues, ...wixForms.initialValues });
    }
  }, [wixForms.initialValues, isBookForSomeoneElseActive]);

  useEffect(() => {
    if (isBookForSomeoneElseActive) {
      setWixFormValues({});
    } else {
      setWixFormValues(wixForms.initialValues);
    }
  }, [isBookForSomeoneElseActive]);

  // The booker (participant 0) mirrors the single-form prefill: seeded with the
  // member details when booking for themselves, cleared when booking for someone
  // else so those details are entered manually. Participants 2..N are untouched here
  // (they start empty). Re-runs when the prefill arrives or the toggle flips.
  useEffect(() => {
    setParticipantForms((prev) => {
      const next = [...prev];
      next[0] = isBookForSomeoneElseActive
        ? {}
        : { ...next[0], ...wixForms.initialValues };
      return next;
    });
  }, [wixForms.initialValues, isBookForSomeoneElseActive]);

  // Member field overrides (prefilled + locked) only apply to the booker's own form.
  // Strip them when booking for someone else, or for participants after the first —
  // those are other people, so their fields must be empty and editable.
  const shouldClearFieldOverrides =
    isBookForSomeoneElseActive ||
    (isCollectParticipantFormActive && activeParticipantIndex > 0);
  const effectiveWixForms = shouldClearFieldOverrides
    ? { ...wixForms, overrides: {} }
    : wixForms;

  const renderedEmptyState = renderEmptyStatePage({
    error,
    status,
    errors,
    serviceData,
    experiments,
  });

  if (renderedEmptyState) {
    return renderedEmptyState;
  }

  const toastError = getErrorByType({
    // The pending submission (NO_VALID_INTAKE_FORM_SUBMISSION) is surfaced via the pending dialog,
    // not as a raw toast.
    errors: errors.filter(
      (e) => e.errorType !== GenericErrorType.NO_VALID_INTAKE_FORM_SUBMISSION,
    ),
    errorType: GenericErrorType,
  });

  const cartConflictError = getErrorByType({
    errors,
    errorType: CartConflictErrorType,
  });

  const intakeFormsArray = mapToArray<IntakeForm>(intakeForms ?? {});

  // Anonymous + a pending-approval intake form → block the booking form entirely and prompt login
  // first (Strategy A, §8): a pending submission must be tracked against a contact. A form is
  // pending-approval when it has trigger fields (MANUAL_APPROVE_TRIGGER_FIELDS) or is set to
  // MANUAL_APPROVE_ALL (every submission pending, no triggers configured).
  const requiresLoginForIntakeForm =
    isIntakeFormsPendingApprovalEnabled &&
    !isLoggedIn &&
    intakeFormsArray.some(
      (form) =>
        form.approvalMode === ApprovalMode.MANUAL_APPROVE_ALL ||
        (form.pendingTriggerFields?.length ?? 0) > 0,
    );
  // Hard block: the booking form is never rendered for an anonymous visitor on a triggered form —
  // only the login modal (no dismiss/bypass). They must log in before anything loads. After a
  // successful login the modal auto-closes (the form then renders).
  if (requiresLoginForIntakeForm && !hasLoggedInViaModal) {
    return (
      <Modal
        dataHook="intake-form-login-modal"
        withoutCloseButton
        modalTitle={t('app.intake-form-section.login-modal.title')}
        modalSubtitle={t('app.intake-form-section.login-modal.subtitle')}
        primaryButton={{
          label: t('app.intake-form-section.login-modal.cta'),
          onClick: async () => {
            const loggedIn = await actions.promptLogin();
            if (loggedIn) {
              setHasLoggedInViaModal(true);
            }
          },
        }}
      />
    );
  }

  const onContactBusiness = () => {
    void actions.contactBusiness();
  };

  // A pending submission no longer blocks the whole form on load — the intake section renders the
  // pending state inline. Only when the customer clicks Book is it rejected with
  // NO_VALID_INTAKE_FORM_SUBMISSION; then the pending dialog is OVERLAID on the form (the form stays
  // behind it) and is dismissible — no redirect (§7.5).
  const hasNoValidIntakeSubmissionError = errors.some(
    (e) => e.errorType === GenericErrorType.NO_VALID_INTAKE_FORM_SUBMISSION,
  );
  const pendingFormName = intakeFormsArray.find(
    (form) =>
      form.status === IntakeFormStatus.PENDING_APPROVAL ||
      form.previousSubmissionStatus === Status.PENDING_APPROVAL,
  )?.formName;
  // Close the dialog without leaving the booking form: clear the book-time error.
  const closePendingModal = () =>
    actions.removeErrors([GenericErrorType.NO_VALID_INTAKE_FORM_SUBMISSION]);

  const showGlobalLoader = () => showGlobalLoaderList.includes(status);

  return (
    <FormViewerContext
      host={widgetProps.host}
      wfActions={widgetProps.wfActions}
      wfConfig={widgetProps.wfConfig}
      wfResults={widgetProps.wfResults}
    >
      <EditorContextProvider value={editorContext} key="form-main-widget">
        <FormActionsProvider
          value={{
            ...actions,
            validateWixForm: async () =>
              validateWixForm({
                actions,
                serviceData,
                wixFormRef,
                intakeForms,
                wixFormValues,
              }),
          }}
        >
          <FormComponentContextProvider
            value={{
              wixFormValues,
              participantFormValues: isCollectParticipantFormActive
                ? participantForms
                : [wixFormValues],
              isCartConflictError: !!cartConflictError,
              isUpsellPluginInstalled,
              dateRegionalSettingsLocale,
            }}
          >
            <Suspense
              fallback={
                <RenderGlobalLoader
                  dataHook={WidgetDataHooks.SUSPENSE_GLOBAL_SPINNER}
                  showGlobalLoader
                />
              }
            >
              <div
                className={st(classes.root, {
                  isMobile,
                  isProcessing: showGlobalLoader(),
                })}
                data-hook={WidgetDataHooks.MAIN_CONTAINER}
              >
                <div className={classes.wrapper}>
                  <RenderGlobalLoader showGlobalLoader={showGlobalLoader()} />
                  {toastError ? <Toast toastError={toastError} /> : null}
                  <BackButton />
                  {isSlotUnavailable && <UnavailableSlotMessage />}
                  <Header />
                  {cartConflictError ? (
                    <CartConflictMessage />
                  ) : (
                    <ServiceInCartNotification
                      isServiceInCart={isServiceInCart}
                    />
                  )}
                  <div className={classes.body}>
                    <div className={classes.formWrapper}>
                      {isCollectParticipantFormActive ? (
                        <>
                          <ParticipantsPager
                            currentIndex={activeParticipantIndex}
                            total={participantForms.length}
                            onSelect={goToParticipant}
                          >
                            {/* The checkbox is shown for every participant. The
                                booker (first) can toggle it and sees the banner;
                                participants 2..N are always someone else, so it's
                                shown checked + disabled with no banner. */}
                            {isBookForSomeoneElseAvailable && (
                              <BookForSomeoneElseCheckbox
                                isBookingForSomeoneElse={
                                  isBookingForSomeoneElse ||
                                  activeParticipantIndex > 0
                                }
                                isPricingPlanSelected={
                                  activeParticipantIndex === 0
                                    ? isPricingPlanSelected(serviceData)
                                    : undefined
                                }
                                disabled={activeParticipantIndex !== 0}
                              />
                            )}
                            {activeParticipantIndex === 0 &&
                              isBookForSomeoneElseActive && (
                                <BookForSomeoneElseBanner />
                              )}
                            {/* Keyed by index so the form remounts per participant,
                                re-applying that participant's values/overrides
                                instead of reusing the previous one's state. */}
                            <WixForm
                              key={activeParticipantIndex}
                              formId={serviceData.formId}
                              wixFormRef={wixFormRef}
                              wixForms={effectiveWixForms}
                              formValues={
                                participantForms[activeParticipantIndex] ?? {}
                              }
                              handleFormChange={handleParticipantFormChange}
                            />
                            <AddonsDetails
                              serviceData={serviceData}
                              participantIndex={activeParticipantIndex}
                            />
                          </ParticipantsPager>
                          <ParticipantActionButton
                            dataHook={ParticipantActionButtonDataHooks.ADD}
                            icon={<Plus aria-hidden />}
                            label={t(
                              'bookings.form.collectParticipant.addParticipant.label',
                            )}
                            onClick={addParticipant}
                            disabled={
                              participantForms.length >= maxParticipants ||
                              isPayingWithPlan
                            }
                            tooltipContent={
                              isPayingWithPlan
                                ? t(
                                    'bookings.form.collectParticipant.addParticipant.planTooltip',
                                  )
                                : participantForms.length >= maxParticipants
                                ? t(
                                    'bookings.form.collectParticipant.addParticipant.maxReachedTooltip',
                                  )
                                : undefined
                            }
                          />
                          {/* activeParticipantIndex > 0 already implies there is
                              more than one participant. */}
                          {activeParticipantIndex > 0 && (
                            <ParticipantActionButton
                              dataHook={ParticipantActionButtonDataHooks.REMOVE}
                              icon={<Delete aria-hidden />}
                              label={t(
                                'bookings.form.collectParticipant.removeParticipant.label',
                              )}
                              onClick={removeParticipant}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {isBookForSomeoneElseAvailable && (
                            <BookForSomeoneElseCheckbox
                              isBookingForSomeoneElse={!!isBookingForSomeoneElse}
                              isPricingPlanSelected={isPricingPlanSelected(
                                serviceData,
                              )}
                            />
                          )}
                          {isBookForSomeoneElseActive && (
                            <BookForSomeoneElseBanner />
                          )}
                          <WixForm
                            formId={serviceData.formId}
                            wixFormRef={wixFormRef}
                            wixForms={effectiveWixForms}
                            formValues={wixFormValues}
                            handleFormChange={handleWixFormChange}
                          />
                          <NumberOfParticipantsDropdown
                            serviceData={serviceData}
                          />
                        </>
                      )}
                      {!isSSR && isServiceExist(formSelectedSlot) && (
                        <PaymentSelectionContainer
                          serviceData={serviceData}
                          memberDetails={memberDetails}
                          numberOfParticipants={selectedNumberOfParticipants}
                          isPricingPlanInstalled={isPricingPlanInstalled}
                          status={status}
                          errors={errors}
                          isCart={isCart}
                          isBookingForSomeoneElse={isBookForSomeoneElseActive}
                          blockPricingPlanOptions={hasMultipleParticipants}
                        />
                      )}
                      <IntakeFormSection
                        intakeForms={intakeForms}
                        errors={errors}
                        isSingleService={serviceData.isSingleService}
                        onContactBusiness={onContactBusiness}
                      />
                      {!isCollectParticipantFormActive && (
                        <AddonsDetails serviceData={serviceData} />
                      )}
                      {isRelatedProductsOnServicesEnabled &&
                        settings.get(
                          settingsParams.relatedProductsSectionVisible,
                        ) && (
                          <RelatedProducts
                            products={relatedProducts}
                            selection={selectedProductAddons}
                            onSelectionChange={actions.setSelectedProductAddons}
                            onProductEnriched={
                              actions.setEnrichedRelatedProduct
                            }
                            storesAppInstance={storesAppInstance}
                          />
                        )}
                    </div>
                    <div className={classes.sidebar}>
                      <div className={classes.floatingContainer}>
                        <BookingDetails
                          serviceData={serviceData}
                          formSelectedSlot={formSelectedSlot}
                          numberOfParticipants={selectedNumberOfParticipants}
                          isCart={isCart}
                          selectedProductAddons={selectedProductAddons}
                          relatedProducts={relatedProducts}
                        />
                        <CouponCheckbox
                          serviceData={serviceData}
                          couponInfo={couponInfo}
                          isServiceInCart={isServiceInCart}
                        />
                        {serviceData.isSingleService && (
                          <PaymentSummary
                            serviceData={serviceData}
                            numberOfParticipants={selectedNumberOfParticipants}
                            status={status}
                            isCart={isCart}
                            selectedProductAddons={selectedProductAddons}
                            relatedProducts={relatedProducts}
                          />
                        )}
                        <AddonsNotification serviceData={serviceData} />
                        <SmsNotification
                          isMSAutomationEnabled={isSMSAutomationsEnabled}
                        />
                        <PolicyDescription serviceData={serviceData} />
                        <ButtonsContainer
                          serviceData={serviceData}
                          isCart={isCart}
                          isServiceInCart={isServiceInCart}
                          errors={errors}
                          status={status}
                          selectedNumberOfParticipants={
                            selectedNumberOfParticipants
                          }
                          existingBookingDetails={existingBookingDetails}
                          memberDetails={memberDetails}
                          isSlotUnavailable={isSlotUnavailable}
                        />
                      </div>
                    </div>
                  </div>
                  {dialog ? (
                    <Dialog
                      {...dialog.props}
                      {...(dialog.type === DialogType.NoAvailablePaymentOptions
                        ? {
                            onConfirm: () => window.history.back(),
                            confirmButtonPrefixIcon: isRTL ? (
                              <ChevronRight aria-hidden />
                            ) : (
                              <ChevronLeft aria-hidden />
                            ),
                          }
                        : {})}
                    />
                  ) : null}
                  {hasNoValidIntakeSubmissionError && (
                    <Modal
                      dataHook="intake-form-pending-modal"
                      withoutCloseButton
                      modalTitle={t('app.intake-form-section.pending-modal.title')}
                      modalSubtitle={t(
                        'app.intake-form-section.pending-modal.subtitle',
                        {
                          formName:
                            pendingFormName ||
                            t(
                              'app.intake-form-section.pending-modal.form-name-fallback',
                            ),
                        },
                      )}
                      primaryButton={{
                        label: t('app.intake-form-section.pending-modal.cta'),
                        onClick: closePendingModal,
                      }}
                      secondaryButton={{
                        label: t(
                          'app.intake-form-section.pending-modal.contact',
                        ),
                        onClick: () => {
                          onContactBusiness();
                          closePendingModal();
                        },
                      }}
                      onClose={closePendingModal}
                    />
                  )}
                  <AlreadyBookedModal
                    existingBookingDetails={existingBookingDetails}
                    memberDetails={memberDetails}
                    serviceData={serviceData}
                  />
                  <CartErrorModal serviceData={serviceData} errors={errors} />
                  {cartModal?.status ? (
                    <CartModal
                      cartModalStatus={cartModal?.status}
                      serviceData={serviceData}
                      formSelectedSlot={formSelectedSlot}
                      bookingIds={cartModal.bookingIds}
                      settings={settings}
                    />
                  ) : null}
                </div>
              </div>
            </Suspense>
          </FormComponentContextProvider>
        </FormActionsProvider>
      </EditorContextProvider>
    </FormViewerContext>
  );
};

const RenderGlobalLoader = ({
  showGlobalLoader,
  dataHook,
}: {
  showGlobalLoader: boolean;
  dataHook?: string;
}) => {
  if (!showGlobalLoader) {
    return null;
  }
  return (
    <>
      <div
        className={st(classes.blanket)}
        data-hook={dataHook ? dataHook : WidgetDataHooks.GLOBAL_SPINNER}
      />
      <EmptyStatePage isProcessing={showGlobalLoader} />
    </>
  );
};

const renderEmptyStatePage = ({
  status,
  errors,
  error,
  serviceData,
  experiments,
}: {
  status: FormStatus;
  errors: FormError[];
  error?: Error | null;
  serviceData?: ServiceData;
  experiments?: ReturnType<typeof useExperiments>['experiments'];
}) => {
  const processingStatuses = [
    FormStatus.INITIALIZING,
    FormStatus.PROCESSING_USER_DETAILS,
    FormStatus.SSR,
  ];
  const isProcessing = processingStatuses.includes(status);

  const shouldShowEmptyStatePage = () =>
    isProcessing ||
    hasErrorOfType({ errorType: EmptyStateErrorType, errors }) ||
    error;

  if (shouldShowEmptyStatePage()) {
    if (error) {
      console.error('Empty state page rendered', error, errors);
    }
    return <EmptyStatePage isProcessing={isProcessing} />;
  }

  if (!serviceData?.slotServices) {
    console.error(
      'Empty state page rendered: some service not valid',
      error,
      errors,
    );
    return (
      <EmptyStatePage
        isProcessing={isProcessing}
        title="app.empty-state-page.no-pp.title"
        subtitle="app.empty-state-page.no-pp.subtitle"
      />
    );
  }

  return null;
};

const hasPricingPlanOnlyService = (serviceData?: ServiceData | null) => {
  if (!serviceData?.slotServices) {
    return false;
  }

  return mapToArray<SlotService>(serviceData.slotServices).some(
    ({ service }) => {
      return (
        service.payment?.offeredAs?.length === 1 &&
        service.payment?.offeredAs?.[0] === OfferedAsType.PRICING_PLAN
      );
    },
  );
};

const isPricingPlanSelected = (serviceData?: ServiceData | null) => {
  if (!serviceData?.slotServices) {
    return false;
  }

  return mapToArray<SlotService>(serviceData.slotServices).some(
    ({ selectedPaymentType }) =>
      selectedPaymentType === PaymentMethod.MEMBERSHIP,
  );
};

export default withSlotsPlaceholders(Widget);
